import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import * as signalR from '@microsoft/signalr';
import { environment } from '../../../environments/environment';
import { TokenManagerService } from './token-manager.service';

export interface ConsultantLocationUpdate {
  consultantId: number;
  latitude: number;
  longitude: number;
  timestamp: string;
}

@Injectable({
  providedIn: 'root',
})
export class ConsultantLocationTrackingService {
  private hubConnection: signalR.HubConnection | null = null;
  private trackedConsultantIds = new Set<number>();
  private readonly locationSubject = new Subject<ConsultantLocationUpdate>();

  readonly location$ = this.locationSubject.asObservable();

  constructor(private readonly tokenManager: TokenManagerService) {}

  startTracking(consultantId: number): void {
    this.startTrackingMany([consultantId]);
  }

  startTrackingMany(consultantIds: number[]): void {
    const normalizedIds = [...new Set(consultantIds.filter((consultantId) => consultantId > 0))];
    if (normalizedIds.length === 0) {
      this.stopTracking();
      return;
    }

    if (!this.hubConnection) {
      this.hubConnection = new signalR.HubConnectionBuilder()
        .withUrl(`${environment.baseUrl}/ws/panel-notifications`, {
          withCredentials: false,
          accessTokenFactory: () => this.tokenManager.getToken() ?? '',
        })
        .withAutomaticReconnect()
        .build();

      this.hubConnection.on('consultantLocationUpdated', (payload: ConsultantLocationUpdate) => {
        this.locationSubject.next(payload);
      });

      this.hubConnection.onreconnected(() => {
        this.joinConsultantGroups(Array.from(this.trackedConsultantIds));
      });

      this.hubConnection
        .start()
        .then(() => {
          this.trackedConsultantIds = new Set(normalizedIds);
          return this.joinConsultantGroups(normalizedIds);
        })
        .catch(() => undefined);

      return;
    }

    const currentIds = new Set(this.trackedConsultantIds);
    const nextIds = new Set(normalizedIds);
    const idsToJoin = normalizedIds.filter((consultantId) => !currentIds.has(consultantId));
    const idsToLeave = Array.from(currentIds).filter((consultantId) => !nextIds.has(consultantId));
    this.trackedConsultantIds = nextIds;

    if (this.hubConnection.state === signalR.HubConnectionState.Connected) {
      this.leaveConsultantGroups(idsToLeave);
      this.joinConsultantGroups(idsToJoin);
    }
  }

  stopTracking(): void {
    if (this.hubConnection) {
      if (this.trackedConsultantIds.size > 0 && this.hubConnection.state === signalR.HubConnectionState.Connected) {
        this.leaveConsultantGroups(Array.from(this.trackedConsultantIds));
      }
      this.hubConnection.stop().catch(() => undefined);
      this.hubConnection = null;
    }
    this.trackedConsultantIds = new Set<number>();
  }

  private joinConsultantGroups(consultantIds: number[]): Promise<void> {
    return Promise.all(
      consultantIds.map((consultantId) =>
        this.hubConnection?.invoke('JoinConsultantTracking', consultantId).catch(() => undefined),
      ),
    ).then(() => undefined);
  }

  private leaveConsultantGroups(consultantIds: number[]): void {
    consultantIds.forEach((consultantId) => {
      this.hubConnection?.invoke('LeaveConsultantTracking', consultantId).catch(() => undefined);
    });
  }
}
